import { jsx as _jsx } from "react/jsx-runtime";
import { Editor } from '@monaco-editor/react';
import { useTheme } from '@vertesia/ui/core';
import debounce from 'debounce';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { registerCustomFoldingProviders } from './foldingProviders.js';
export class VEditorApi {
    VgetValue;
    VsetValue;
    constructor(VgetValue, VsetValue) {
        this.VgetValue = VgetValue;
        this.VsetValue = VsetValue;
    }
    getValue() {
        return this.VgetValue();
    }
    setValue(value) {
        this.VsetValue(value || '');
    }
}
export function MonacoEditor({ onChange, value, className, editorRef, language = 'javascript', debounceTimeout = 0, options = {}, beforeMount, onMount, defaultValue, useCustomFolding = false, }) {
    const [editorValue, setEditorValue] = useState(value || defaultValue || '');
    const editorInstanceRef = useRef(null);
    const monacoInstanceRef = useRef(null);
    const { theme } = useTheme();
    const resolvedTheme = theme === 'system'
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : theme;
    const getValueRef = useRef(() => editorValue);
    const setValueRef = useRef((newValue) => {
        setEditorValue(newValue);
        if (editorInstanceRef.current) {
            editorInstanceRef.current.setValue(newValue);
        }
    });
    useEffect(() => {
        getValueRef.current = () => editorValue;
    }, [editorValue]);
    useEffect(() => {
        if (editorRef) {
            editorRef.current = new VEditorApi(() => getValueRef.current(), (value) => setValueRef.current(value));
        }
        return () => {
            if (editorRef) {
                editorRef.current = undefined;
            }
        };
    }, [editorRef]);
    const debouncedOnChange = useMemo(() => {
        if (!onChange)
            return undefined;
        if (debounceTimeout > 0) {
            return debounce((update) => onChange(update), debounceTimeout);
        }
        else {
            return onChange;
        }
    }, [onChange, debounceTimeout]);
    const foldAllCodeBlocks = useCallback(async (editor, monacoInstance) => {
        const model = editor.getModel();
        if (!model)
            return;
        const codeBlockRegExp = /```[\s\S]*?```/g;
        let match;
        while ((match = codeBlockRegExp.exec(model.getValue())) !== null) {
            const startLine = model.getPositionAt(match.index).lineNumber;
            const endLine = model.getPositionAt(match.index + match[0].length).lineNumber;
            editor.setSelection(new monacoInstance.Selection(startLine, 1, endLine, 1));
            await editor.getAction('editor.createFoldingRangeFromSelection')?.run();
        }
    }, []);
    const handleEditorChange = useCallback((newValue) => {
        const actualValue = newValue || '';
        setEditorValue(actualValue);
        if (debouncedOnChange) {
            const update = {
                docChanged: true,
                state: {
                    doc: {
                        toString: () => actualValue,
                        length: actualValue.length
                    }
                }
            };
            // Using type assertion through unknown to avoid complex type mocking
            debouncedOnChange(update);
        }
    }, [debouncedOnChange]);
    const handleEditorDidMount = useCallback((editor, monacoInstance) => {
        editorInstanceRef.current = editor;
        monacoInstanceRef.current = monacoInstance;
        if (useCustomFolding) {
            registerCustomFoldingProviders(monacoInstance);
        }
        // Update the setValue ref to use the actual editor instance
        setValueRef.current = (newValue) => {
            setEditorValue(newValue);
            editor.setValue(newValue);
        };
        // Set up custom theme for better error line highlighting
        monacoInstance.editor.defineTheme('errorLineTheme', {
            base: resolvedTheme === 'dark' ? 'vs-dark' : 'vs',
            inherit: true,
            rules: [],
            colors: {
                'editorError.background': '#ffebee',
                'editorError.border': '#f44336',
            },
        });
        monacoInstance.editor.setTheme('errorLineTheme');
        if (useCustomFolding) {
            setTimeout(() => foldAllCodeBlocks(editor, monacoInstance), 300);
        }
        // Call custom onMount if provided
        onMount?.(editor, monacoInstance);
    }, [onMount, resolvedTheme, useCustomFolding, foldAllCodeBlocks]);
    // Update editor value when prop changes from outside
    useEffect(() => {
        const effectiveValue = value || defaultValue || '';
        if (effectiveValue !== editorValue) {
            setEditorValue(effectiveValue);
            if (editorInstanceRef.current) {
                editorInstanceRef.current.setValue(effectiveValue);
            }
        }
    }, [value]); // Only depend on value prop, not editorValue
    // Re-fold code blocks when value prop changes externally
    useEffect(() => {
        if (!useCustomFolding || !editorInstanceRef.current || !monacoInstanceRef.current)
            return;
        const editor = editorInstanceRef.current;
        const monacoInstance = monacoInstanceRef.current;
        const timer = setTimeout(() => foldAllCodeBlocks(editor, monacoInstance), 300);
        return () => clearTimeout(timer);
    }, [value, useCustomFolding, foldAllCodeBlocks]);
    const defaultOptions = {
        fontSize: 14,
        fontFamily: 'monospace',
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        lineNumbers: 'on',
        folding: true,
        lineDecorationsWidth: 10,
        lineNumbersMinChars: 3,
        automaticLayout: true,
        formatOnPaste: true,
        formatOnType: true,
        tabSize: 2,
        insertSpaces: true,
        fixedOverflowWidgets: true, // Hover/diagnostic popovers float outside the editor bounds
        glyphMargin: true, // Enable better error reporting
        renderValidationDecorations: 'on', // Show error squiggles
        renderLineHighlight: 'line', // Highlight entire line for errors
        hover: {
            enabled: true,
            delay: 100
        }, // Enable hover for error messages
        quickSuggestions: {
            other: true,
            comments: true,
            strings: true
        }, // Show problems panel information
        ...options
    };
    return (_jsx("div", { className: clsx(className, 'w-full h-full!'), children: _jsx(Editor, { className: "h-full w-full", height: "100%", theme: resolvedTheme === 'dark' ? 'vs-dark' : 'light', language: language, value: editorValue, onChange: handleEditorChange, onMount: handleEditorDidMount, beforeMount: beforeMount, options: defaultOptions, defaultValue: defaultValue || '' }) }));
}
//# sourceMappingURL=MonacoEditor.js.map